import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useParams, useNavigate } from 'react-router-dom';
import { Container, Row, Col, Card } from 'react-bootstrap';
import TryOn from './TryOn';
import SEO from './SEO';
import { supabase } from '../supabaseClient';
import Nav from './Nav';
import '../assets/css/psearch.css';
import '../assets/css/about.css';
import SignInModal from './SignInModal';
import { useCurrency } from '../context/CurrencyContext';

const BRAND_ACCENT = '#555577';
const BRAND_TEXT = '#1a1a1a';
const IMAGE_BG = '#f5f5f5';
const DIVIDER = '#555577';


const API_BASE_URL = process.env.NODE_ENV === 'production'
  ? 'https://yemberzal-app-28481d1d0b39.herokuapp.com/api'
  : 'http://127.0.0.1:8000/api';

function BrandPage() {
  const { brandName } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { currency, getCurrencySymbol } = useCurrency();
  const [brandProducts, setBrandProducts] = useState([]);
  const [brandImage, setBrandImage] = useState('');
  const [brandProfile, setBrandProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [brandWebsite, setBrandWebsite] = useState('');
  const [user, setUser] = useState(null);
  const [savedProducts, setSavedProducts] = useState([]);
  const [savedBrands, setSavedBrands] = useState([]);
  const [showSignInModal, setShowSignInModal] = useState(false);
  const [signInModalMessage, setSignInModalMessage] = useState('');
  // const [showUserDropdown, setShowUserDropdown] = useState(false);
  const [tryOnOpen, setTryOnOpen] = useState(false);
  const [tryOnImage, setTryOnImage] = useState(null);
  const [pendingTryOnImage, setPendingTryOnImage] = useState(null);
  const [authEmail, setAuthEmail] = useState('');
  const [authLoading, setAuthLoading] = useState(false);
  const [authMessage, setAuthMessage] = useState('');
  const [focusedProductId, setFocusedProductId] = useState(null);

  const focusProductIdFromUrl = useMemo(() => {
    const params = new URLSearchParams(location.search);
    const val = params.get('productId');
    return val ? String(val) : '';
  }, [location.search]);

  const normalizeProductId = (productId) => {
    if (productId === null || productId === undefined) return '';
    return String(productId);
  };

  const handleGoogleSignIn = async () => {
    try {
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: `${window.location.origin}/auth/callback`,
        },
      });

      if (error) {
        setAuthMessage(`Error: ${error.message}`);
      }
    } catch (err) {
      setAuthMessage(`Error: ${err.message}`);
    }
  };

  const handleEmailSignIn = async (e) => {
    e.preventDefault();
    if (!authEmail) return;

    setAuthLoading(true);
    setAuthMessage('');

    const { error } = await supabase.auth.signInWithOtp({
      email: authEmail,
      options: {
        emailRedirectTo: `${window.location.origin}/auth/callback`,
      },
    });

    if (error) {
      setAuthMessage(`Error: ${error.message}`);
    } else {
      setAuthMessage('Check your email for the magic link!');
    }

    setAuthLoading(false);
  };

  const handleEmailPasswordSignIn = async (email, password) => {
    setAuthLoading(true);
    setAuthMessage('');
    
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    
    if (error) {
      setAuthMessage(`Error: ${error.message}`);
    } else {
      setShowSignInModal(false);
    }
    setAuthLoading(false);
  };

  const handleEmailPasswordSignUp = async (email, password) => {
    setAuthLoading(true);
    setAuthMessage('');
    
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: `${window.location.origin}/auth/callback`,
        data: { user_role: 'general' },
      },
    });
    
    if (error) {
      setAuthMessage(`Error: ${error.message}`);
    } else {
      setAuthMessage('Check your email to confirm your account!');
    }
    setAuthLoading(false);
  };

  // Sign out logic
  // const handleSignOut = async () => {
  //   const { error } = await supabase.auth.signOut();
  //   if (error) {
  //     console.error('Error signing out:', error.message);
  //   }
  // };

  // For try-on feature (match ProductSearch.js logic)
  const handleTryOn = (image) => {
    if (!user) {
      setSignInModalMessage('Sign in to try it on and save your look.');
      setPendingTryOnImage(image);
      setShowSignInModal(true);
      return;
    }
    setTryOnImage(image);
    setTryOnOpen(true);
  };

  // Open try-on after successful sign in if there was a pending image
  useEffect(() => {
    if (user && pendingTryOnImage) {
      setTryOnImage(pendingTryOnImage);
      setTryOnOpen(true);
      setPendingTryOnImage(null);
      setShowSignInModal(false);
    }
  }, [user, pendingTryOnImage]);

  // Save/unsave logic
  const toggleSaveProduct = async (productId) => {
    if (!user) {
      setSignInModalMessage('Sign in to save your favorite product.');
      setShowSignInModal(true);
      return;
    }
    const normalizedProductId = normalizeProductId(productId);
    if (!normalizedProductId) return;
    const isSaved = savedProducts.includes(normalizedProductId);
    try {
      if (isSaved) {
        await supabase
          .from('saved_products')
          .delete()
          .eq('user_id', user.id)
          .eq('product_id', normalizedProductId);
        setSavedProducts(savedProducts.filter(id => id !== normalizedProductId));
      } else {
        await supabase
          .from('saved_products')
          .insert({ user_id: user.id, product_id: normalizedProductId });
        setSavedProducts([...savedProducts, normalizedProductId]);
      }
    } catch (error) {
      console.error('Error toggling saved product:', error);
    }
  };

  // Save/unsave brand logic
  const toggleSaveBrand = async () => {
    if (!user) {
      setSignInModalMessage('Sign in to save your favorite brand.');
      setShowSignInModal(true);
      return;
    }
    const currentBrandName = brandProfile?.name || brandName;
    if (!currentBrandName) return;
    const isSaved = savedBrands.includes(currentBrandName);
    try {
      if (isSaved) {
        await supabase
          .from('saved_brands')
          .delete()
          .eq('user_id', user.id)
          .eq('brand_name', currentBrandName);
        setSavedBrands(savedBrands.filter(brand => brand !== currentBrandName));
      } else {
        await supabase
          .from('saved_brands')
          .insert({ user_id: user.id, brand_name: currentBrandName });
        setSavedBrands([...savedBrands, currentBrandName]);
      }
    } catch (error) {
      console.error('Error toggling saved brand:', error);
    }
  };

  useEffect(() => {
    const getSession = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      setUser(session?.user ?? null);
      if (session?.user) {
        loadSavedProducts(session.user.id);
      }
    };
    getSession();
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      setUser(session?.user ?? null);
      if (session?.user) {
        loadSavedProducts(session.user.id);
        loadSavedBrands(session.user.id);
      } else {
        setSavedProducts([]);
        setSavedBrands([]);
      }
    });
    return () => subscription.unsubscribe();
    // eslint-disable-next-line
  }, []);

  const loadSavedProducts = async (userId) => {
    try {
      const { data, error } = await supabase
        .from('saved_products')
        .select('product_id')
        .eq('user_id', userId);
      if (data && !error) {
        setSavedProducts(data.map(item => normalizeProductId(item.product_id)).filter(Boolean));
      }
    } catch (error) {
      console.error('Error loading saved products:', error);
    }
  };

  const loadSavedBrands = async (userId) => {
    try {
      const { data, error } = await supabase
        .from('saved_brands')
        .select('brand_name')
        .eq('user_id', userId);
      if (data && !error) {
        setSavedBrands(data.map(item => item.brand_name).filter(Boolean));
      }
    } catch (error) {
      console.error('Error loading saved brands:', error);
    }
  };

  // Fetch public brand profile (name, bio, website, instagram, founder, logo)
  useEffect(() => {
    const fetchBrandProfile = async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/brands/public/${encodeURIComponent(brandName)}/`);
        if (res.ok) {
          const data = await res.json();
          setBrandProfile(data);
        } else {
          setBrandProfile(null);
        }
      } catch (err) {
        setBrandProfile(null);
      }
    };
    fetchBrandProfile();
  }, [brandName]);

  useEffect(() => {
    const fetchBrandProducts = async () => {
      setLoading(true);
      try {
        // Fetch products for this brand
        const response = await fetch(`${API_BASE_URL}/products/search/?brand=${encodeURIComponent(brandName)}&currency=${currency}`);
        if (response.ok) {
          const data = await response.json();
          setBrandProducts(data);
          // Try to get a brand image from the first product (fallback when no profile/logo)
          if (data.length > 0 && data[0].image) {
            setBrandImage(data[0].image);
          }
          // Try to extract a brand website/Instagram from the first product's link (fallback)
          if (data.length > 0 && data[0].link) {
            setBrandWebsite(data[0].link);
          } else {
            setBrandWebsite('');
          }
        }
      } catch (error) {
        setBrandProducts([]);
        setBrandWebsite('');
      } finally {
        setLoading(false);
      }
    };
    fetchBrandProducts();
  }, [brandName]);

  useEffect(() => {
    if (!focusProductIdFromUrl) return;
    if (!brandProducts || brandProducts.length === 0) return;

    const el = document.getElementById(`brand-product-${focusProductIdFromUrl}`);
    if (el) {
      setFocusedProductId(focusProductIdFromUrl);
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      const t = setTimeout(() => setFocusedProductId(null), 2500);
      return () => clearTimeout(t);
    }
  }, [focusProductIdFromUrl, brandProducts]);

  const DEFAULT_CATEGORIES = ['Menswear', 'Womenswear', 'Pashmina'];

  const brandCategories = useMemo(() => {
    const fromProfile = (brandProfile?.categories || '').toString().trim();
    if (fromProfile) {
      const list = fromProfile
        .split(/,\s*/)
        .map((c) => c.trim())
        .filter(Boolean)
        .map((c) => c.charAt(0).toUpperCase() + c.slice(1).toLowerCase());
      const unique = [...new Set(list)];
      if (unique.length > 0) return unique.length > 5 ? unique.slice(0, 5) : unique;
    }
    if (!brandProducts || brandProducts.length === 0) return DEFAULT_CATEGORIES;
    const set = new Set();
    brandProducts.forEach((p) => {
      const raw = (p.category || '').toString().trim();
      if (!raw || raw.toLowerCase() === 'uncategorized') return;
      raw.split(/,\s*/).forEach((c) => {
        const trimmed = c.trim();
        if (trimmed) set.add(trimmed.charAt(0).toUpperCase() + trimmed.slice(1).toLowerCase());
      });
    });
    if (set.size > 0) {
      const list = Array.from(set);
      return list.length > 5 ? list.slice(0, 5) : list;
    }
    return DEFAULT_CATEGORIES;
  }, [brandProfile?.categories, brandProducts]);

  const heroImage = brandProfile?.logo || brandImage || (brandProducts?.[0]?.image) || '';

  const displayBrandName = brandProfile?.name || decodeURIComponent(brandName);
  const brandJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Brand',
    name: displayBrandName,
    url: `https://theyemberzal.com/brand/${encodeURIComponent(displayBrandName)}`,
    ...(heroImage && { logo: heroImage }),
    ...(brandProfile?.description && { description: brandProfile.description }),
    ...(brandProfile?.website && { sameAs: [brandProfile.website] }),
  };

  return (
    <>
      <SEO
        title={`${displayBrandName} — Kashmiri Fashion Brand`}
        description={brandProfile?.description ? brandProfile.description.slice(0, 155) : `Shop ${displayBrandName}'s collection of authentic Kashmiri fashion on Yemberzal. Browse products, try them on virtually, and discover Kashmir's finest craftsmanship.`}
        path={`/brand/${encodeURIComponent(displayBrandName)}`}
        image={heroImage || undefined}
        jsonLd={brandJsonLd}
      />
     <Container className="py-5 font-raleway">
      <Nav/>
      <div className="container py-5">
      {tryOnOpen && user && (
        <TryOn
          productImage={tryOnImage}
          onClose={() => {
            setTryOnOpen(false);
            setTryOnImage(null);
          }}
          user={user}
        />
      )}
      {/* Two-column hero: left = image, right = brand info */}
      <div className="bp-hero" style={{
        display: 'flex',
        gap: '60px',
        marginBottom: '60px',
        minHeight: '580px',
      }}>
        {/* Left Image */}
        <div className="bp-hero-image" style={{
          flex: '1 1 45%',
          overflow: 'hidden',
          borderRadius: '4px',
          backgroundColor: '#f5f5f5',
        }}>
          {heroImage ? (
            <img
              src={heroImage}
              alt={brandProfile?.name ?? brandName}
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                objectPosition: 'center center',
              }}
              onError={(e) => {
                e.currentTarget.onerror = null;
                e.currentTarget.src = brandImage || '/default-image.avif';
              }}
            />
          ) : (
            <div style={{ color: '#999', fontSize: '1rem', display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>No image</div>
          )}
        </div>

        {/* Right Content */}
        <div className="bp-hero-content" style={{
          flex: '1 1 55%',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          paddingTop: '20px',
          paddingBottom: '20px',
        }}>
          {/* Category Tags */}
          {brandCategories.length > 0 && (
            <div style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: '6px',
              marginTop: '20px',
              marginBottom: '20px'
            }}>
              {brandCategories.map((cat, index) => (
                <span key={index} style={{
                  display: 'inline-block',
                  fontSize: '12px',
                  fontFamily: 'Montserrat, sans-serif',
                  color: '#666',
                  backgroundColor: '#f5f5f5',
                  padding: '4px 12px',
                  borderRadius: '16px',
                  letterSpacing: '0.3px',
                }}>
                  {cat}
                </span>
              ))}
            </div>
          )}

          {/* Brand Name */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <h1 className="bp-brand-name" style={{
              fontSize: '3rem',
              fontWeight: '400',
              fontFamily: 'Montserrat, sans-serif',
              color: '#1a1a1a',
              margin: '0 0 8px 0',
              lineHeight: '1.1',
            }}>
              {(brandProfile?.name ?? brandName) || ''}
            </h1>
            
            {/* Bookmark Icon */}
            <button
              onClick={toggleSaveBrand}
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                padding: '8px',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'all 0.2s',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = 'rgba(0,0,0,0.05)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
              }}
              title={savedBrands.includes(brandProfile?.name || brandName) ? 'Remove from saved brands' : 'Save brand to favorites'}
            >
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill={savedBrands.includes(brandProfile?.name || brandName) ? '#e63946' : 'none'}
                stroke={savedBrands.includes(brandProfile?.name || brandName) ? '#e63946' : '#666'}
                strokeWidth="2"
              >
                <path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z"/>
              </svg>
            </button>
          </div>

          {/* Tagline */}
          {brandProfile?.tagline && (
            <p style={{
              fontSize: '1.1rem',
              fontFamily: 'Montserrat, sans-serif',
              fontStyle: 'italic',
              color: '#555',
              margin: '0 0 24px 0',
            }}>
              {brandProfile.tagline}
            </p>
          )}

          {/* Description */}
          {brandProfile?.description && (
            <p style={{
              fontSize: '0.95rem',
              fontFamily: 'Montserrat, sans-serif',
              color: '#444',
              lineHeight: 1.7,
              whiteSpace: 'pre-wrap',
              margin: '0 0 28px 0',
              maxWidth: '480px',
            }}>
              {brandProfile.description}
            </p>
          )}

          {/* Details with Icons */}
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '12px',
            marginBottom: '32px',
          }}>
            {/* Location */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#888" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                <circle cx="12" cy="10" r="3" />
              </svg>
              <span style={{ fontSize: '14px', fontFamily: 'Montserrat, sans-serif', color: '#555' }}>
                {brandProfile?.location || 'Srinagar, Kashmir'}
              </span>
            </div>

            {/* Product Count */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#888" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
                <polyline points="3.27 6.96 12 12.01 20.73 6.96" />
                <line x1="12" y1="22.08" x2="12" y2="12" />
              </svg>
              <span style={{ fontSize: '14px', fontFamily: 'Montserrat, sans-serif', color: '#555' }}>
                {brandProducts.length} {brandProducts.length === 1 ? 'product' : 'products'} listed
              </span>
            </div>

            {/* Instagram */}
            {brandProfile?.instagram_handle && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#888" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="2" y="2" width="20" height="20" rx="5" ry="5" />
                  <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z" />
                  <line x1="17.5" y1="6.5" x2="17.51" y2="6.5" />
                </svg>
                <a
                  href={`https://instagram.com/${brandProfile.instagram_handle.replace(/^@/, '')}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ fontSize: '14px', fontFamily: 'Montserrat, sans-serif', color: '#555', textDecoration: 'none' }}
                >
                  @{brandProfile.instagram_handle.replace(/^@/, '')}
                </a>
              </div>
            )}
          </div>

          {/* Visit Brand Website Button */}
          {(brandProfile?.website || brandWebsite) && (
            <a
              href={brandProfile?.website || brandWebsite}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                padding: '16px 32px',
                backgroundColor: '#1a1a1a',
                color: '#fff',
                borderRadius: '4px',
                textDecoration: 'none',
                fontSize: '14px',
                fontWeight: '500',
                fontFamily: 'Montserrat, sans-serif',
                letterSpacing: '0.5px',
                transition: 'background-color 0.2s',
                maxWidth: '480px',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#1a1a1a'; }}
              onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#1a1a1a'; }}
            >
              Visit Brand Website
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                <polyline points="15 3 21 3 21 9" />
                <line x1="10" y1="14" x2="21" y2="3" />
              </svg>
            </a>
          )}
        </div>
      </div>
      <h3
        className="mb-4"
        style={{ fontWeight: 300, fontSize: '1.5rem', color: BRAND_TEXT }}
      >
        Products by <span className="yb-brand-name">{(brandProfile?.name ?? brandName) || ''}</span>
      </h3>
      {loading ? (
        <p style={{ color: '#888' }}>Loading products...</p>
      ) : brandProducts.length === 0 ? (
        <p style={{ color: '#888' }}>No products found for this brand.</p>
      ) : (
        <Row style={{ display: 'flex', gap: '0', justifyContent: 'center' }}>
          {brandProducts.map((product, index) => (
            <Col md={4} key={index} style={{ padding: '0 12px', marginBottom: '24px' }}>
              <div
                id={`brand-product-${String(product.id ?? product.link)}`}
                style={{ 
                  cursor: 'pointer',
                  outline: focusedProductId && focusedProductId === String(product.id ?? product.link) ? `2px solid ${BRAND_ACCENT}` : 'none',
                  outlineOffset: 2,
                }}
                onClick={() => navigate(`/product/${product.id || encodeURIComponent(product.link)}`)}
              >
                {/* Image */}
                <div className="bp-product-image" style={{
                  width: '100%',
                  height: '580px',
                  overflow: 'hidden',
                  borderRadius: '4px',
                  backgroundColor: '#f5f5f5',
                  position: 'relative',
                }}>
                  {product.image && (
                    <img
                      src={product.image}
                      alt={product.title || 'Product Image'}
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        objectPosition: 'center top',
                        transition: 'transform 0.3s ease',
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = 'scale(1.03)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = 'scale(1)';
                      }}
                      onError={(e) => {
                        e.currentTarget.onerror = null;
                        e.currentTarget.src = '/default-image.avif';
                      }}
                    />
                  )}

                  {/* Heart Save Icon */}
                  <button
                    style={{
                      position: 'absolute',
                      top: '12px',
                      right: '18px',
                      background: 'rgba(255,255,255,0.95)',
                      border: 'none',
                      borderRadius: '50%',
                      width: '36px',
                      height: '36px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'pointer',
                      zIndex: 10,
                      boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                      transition: 'all 0.2s',
                    }}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      if (!user) {
                        setSignInModalMessage('Sign in to save your favorite product.');
                        setShowSignInModal(true);
                        return;
                      }
                      toggleSaveProduct(product.id ?? product.link);
                    }}
                    title={savedProducts && savedProducts.includes(normalizeProductId(product.id ?? product.link)) ? 'Remove from saved' : 'Save product'}
                  >
                    <svg
                      width="22"
                      height="22"
                      viewBox="0 0 24 24"
                      fill={savedProducts && savedProducts.includes(normalizeProductId(product.id ?? product.link)) ? '#e63946' : 'none'}
                      stroke="#e63946"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M12 21s-7-4.35-7-10A5 5 0 0 1 12 6a5 5 0 0 1 7 5c0 5.65-7 10-7 10z" />
                    </svg>
                  </button>
                  
                  {/* Try it on Button */}
                  <button
                    style={{
                      position: 'absolute',
                      bottom: '12px',
                      left: '50%',
                      transform: 'translateX(-50%)',
                      padding: '8px 14px',
                      borderRadius: '999px',
                      border: 'none',
                      background: 'rgba(0,0,0,0.7)',
                      color: '#fff',
                      fontSize: '14px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      cursor: 'pointer',
                      backdropFilter: 'blur(6px)',
                    }}
                    onClick={(e) => { e.stopPropagation(); handleTryOn(product.image); }}
                  >
                    Try it on
                  </button>
                </div>

                {/* Product Info */}
                <div style={{ paddingTop: '14px' }}>
                  <h3 style={{
                    fontSize: '15px',
                    fontWeight: '600',
                    fontFamily: 'Montserrat, sans-serif',
                    color: '#1a1a1a',
                    margin: '0 0 4px 0',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                  }}>
                    {product.brand || 'Unknown Brand'}
                  </h3>
                  <p style={{
                    fontSize: '13px',
                    fontFamily: 'Montserrat, sans-serif',
                    color: '#888',
                    margin: '0 0 4px 0',
                  }}>
                    {product.title || 'Unnamed Product'}
                  </p>
                  <p style={{
                    fontSize: '13px',
                    fontFamily: 'Montserrat, sans-serif',
                    color: '#888',
                    margin: 0,
                  }}>
                    {getCurrencySymbol()} {product.price || 'Not Available'}
                  </p>
                </div>
              </div>
            </Col>
          ))}
        </Row>
      )}
      </div>

      <SignInModal
        open={showSignInModal}
        onClose={() => {
          setShowSignInModal(false);
          setAuthMessage('');
          setAuthEmail('');
          setSignInModalMessage('');
        }}
        message={signInModalMessage}
        authEmail={authEmail}
        setAuthEmail={setAuthEmail}
        authLoading={authLoading}
        authMessage={authMessage}
        onEmailSignIn={handleEmailSignIn}
        onGoogleSignIn={handleGoogleSignIn}
        onEmailPasswordSignIn={handleEmailPasswordSignIn}
        onEmailPasswordSignUp={handleEmailPasswordSignUp}
      />

      <style>{`
        @media (max-width: 1200px) {
          .bp-hero {
            gap: 40px !important;
            margin-bottom: 48px !important;
          }
        }

        @media (max-width: 1024px) {
          .bp-hero {
            gap: 32px !important;
            margin-bottom: 40px !important;
          }
          .bp-hero-content {
            padding-top: 0 !important;
            padding-bottom: 0 !important;
          }
        }

        @media (max-width: 768px) {
          .bp-hero {
            flex-direction: column !important;
            gap: 24px !important;
            min-height: auto !important;
            margin-bottom: 32px !important;
          }
          .bp-hero-image {
            flex: none !important;
            width: 100% !important;
            height: 350px !important;
          }
          .bp-hero-content {
            flex: none !important;
            width: 100% !important;
            padding: 0 !important;
            text-align: center !important;
          }
          .bp-brand-name {
            font-size: 2rem !important;
            text-align: center !important;
            justify-content: center !important;
          }
          .bp-product-image {
            width: 100% !important;
            height: 400px !important;
          }
          .bp-hero-content > div[style*="align-items: center"] {
            justify-content: center !important;
          }
          .bp-hero-content > div[style*="display: flex"] {
            flex-direction: column !important;
            align-items: center !important;
            gap: 12px !important;
          }
        }

        @media (max-width: 480px) {
          .bp-hero-image {
            height: 280px !important;
          }
          .bp-brand-name {
            font-size: 1.6rem !important;
          }
          .bp-product-image {
            height: 320px !important;
          }
          .bp-hero-content {
            text-align: center !important;
          }
          .bp-hero-content p {
            text-align: center !important;
            margin-left: auto !important;
            margin-right: auto !important;
          }
          .bp-hero-content > div[style*="flex-direction: column"] {
            align-items: center !important;
          }
        }

        @media (max-width: 360px) {
          .bp-hero-image {
            height: 240px !important;
          }
          .bp-brand-name {
            font-size: 1.4rem !important;
          }
          .bp-product-image {
            height: 280px !important;
          }
          .container {
            padding-left: 8px !important;
            padding-right: 8px !important;
          }
        }
      `}</style>
     </Container>
    </>
  );
}

export default BrandPage;
